import type { LayerType } from '../types';
import type { AudioEngine } from '../audio/engine';
import type { UnifiedGridVisualizer } from '../ui/visualizer';

export interface LayerState {
  id: string;
  type: LayerType;
  label: string;
  muted: boolean;
  volume: number;
  isAdditional?: boolean;
}

/**
 * Sync layer states with visualizer
 */
export function syncVisualizerLayerStates(
  layerStates: LayerState[],
  unifiedVisualizer: UnifiedGridVisualizer | null
): void {
  if (unifiedVisualizer) {
    unifiedVisualizer.updateLayerStates(
      layerStates.map(s => ({ type: s.type, muted: s.muted, volume: s.volume }))
    );
  }
}

/**
 * Update volume for a layer type
 */
export function updateLayerTypeVolume(
  layerType: LayerType,
  layerStates: LayerState[],
  audioEngine: AudioEngine
): void {
  const layersOfType = layerStates.filter(l => l.type === layerType);
  const totalVolume = layersOfType.reduce((sum, l) => {
    return sum + (l.muted ? 0 : l.volume);
  }, 0);
  const normalizedVolume = Math.min(totalVolume, 1.0);
  audioEngine.setLayerVolume(layerType, normalizedVolume);
}

/**
 * Show add layer menu
 * NOTE: This is a stub. Full implementation requires complex state management.
 */
export function showAddLayerMenu(onAddLayer: (type: LayerType) => void): void {
  const existingMenu = document.querySelector('.add-layer-menu');
  if (existingMenu) {
    existingMenu.remove();
    return;
  }

  const btn = document.getElementById('add-layer-btn');
  if (!btn) return;

  const menu = document.createElement('div');
  menu.className = 'add-layer-menu';
  menu.innerHTML = `
    <button class="flex items-center justify-center gap-1 px-3 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-card)] text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]" data-type="percussion">+ PERC</button>
    <button class="flex items-center justify-center gap-1 px-3 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-card)] text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]" data-type="bass">+ BASS</button>
    <button class="flex items-center justify-center gap-1 px-3 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-card)] text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]" data-type="melody">+ MELO</button>
    <button class="flex items-center justify-center gap-1 px-3 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-card)] text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]" data-type="pad">+ PAD</button>
  `;

  document.body.appendChild(menu);

  const rect = btn.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.left = `${rect.left}px`;

  menu.querySelectorAll('button').forEach(button => {
    button.addEventListener('click', () => {
      const type = button.dataset.type as LayerType;
      onAddLayer(type);
      menu.remove();
    });
  });

  setTimeout(() => {
    document.addEventListener('click', () => menu.remove(), { once: true });
  }, 0);
}

/**
 * Render layers list
 * NOTE: Simplified version. Full implementation needs all event handlers.
 */
export function renderLayersList(
  layerStates: LayerState[],
  onMuteToggle: (layerId: string) => void,
  onVolumeChange: (layerId: string, volume: number) => void,
  onRemove: (layerId: string) => void
): void {
  const listEl = document.getElementById('layers-list');
  if (!listEl) return;

  listEl.innerHTML = layerStates.map((layer) => {
    const volumePercent = Math.round(layer.volume * 100);
    const canRemove = layer.isAdditional;
    const mutedClasses = layer.muted ? 'muted' : '';
    const additionalClass = layer.isAdditional ? 'additional-layer' : '';

    return `
      <div class="control-row-inline ${additionalClass}" data-layer-id="${layer.id}" data-layer="${layer.type}">
        <button class="mute-btn ${mutedClasses}" data-layer-id="${layer.id}" data-layer="${layer.type}" title="${layer.muted ? 'Unmute' : 'Mute'}">
          <span class="mute-icon" data-layer-id="${layer.id}"></span>
        </button>
        <span class="control-label">${layer.label}${canRemove ? ' <button class="remove-layer-btn" data-layer-id="' + layer.id + '" title="Remove">×</button>' : ''}</span>
        <input type="range" class="slider-input layer-volume" data-layer-id="${layer.id}" data-layer="${layer.type}" min="0" max="100" value="${volumePercent}" />
      </div>
    `;
  }).join('');

  // Attach event listeners
  listEl.querySelectorAll('.mute-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const layerId = (btn as HTMLElement).dataset.layerId;
      if (layerId) onMuteToggle(layerId);
    });
  });

  listEl.querySelectorAll('.layer-volume').forEach(slider => {
    slider.addEventListener('input', (e) => {
      const layerId = (slider as HTMLElement).dataset.layerId;
      const volume = parseInt((e.target as HTMLInputElement).value) / 100;
      if (layerId) onVolumeChange(layerId, volume);
    });
  });

  listEl.querySelectorAll('.remove-layer-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const layerId = (btn as HTMLElement).dataset.layerId;
      if (layerId) onRemove(layerId);
    });
  });
}
